import { useCallback, useEffect, useState } from 'react';

interface InvestigationConversationFocusOptions {
  investigationId: string;
  evidenceOpen: boolean;
  pinnedEvidenceOpen: boolean;
  dialogOpen: boolean;
  onCloseEvidence: () => void;
  onClosePinnedEvidence: () => void;
}

/**
 * Controls the explicit Investigation Conversation focus mode.
 *
 * Escape follows the natural layer order:
 * 1. close answer evidence;
 * 2. close pinned evidence;
 * 3. leave dialogs alone so their own focus/escape behavior wins;
 * 4. otherwise return to the Investigation overview.
 */
export function useInvestigationConversationFocus({
  investigationId,
  evidenceOpen,
  pinnedEvidenceOpen,
  dialogOpen,
  onCloseEvidence,
  onClosePinnedEvidence,
}: InvestigationConversationFocusOptions) {
  const [isFocused, setIsFocused] = useState(false);

  const focusConversation = useCallback(() => setIsFocused(true), []);
  const backToInvestigation = useCallback(() => setIsFocused(false), []);
  const toggleFocus = useCallback(() => setIsFocused((value) => !value), []);

  useEffect(() => {
    setIsFocused(false);
  }, [investigationId]);

  useEffect(() => {
    if (!isFocused) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      if (evidenceOpen) {
        event.preventDefault();
        onCloseEvidence();
        return;
      }

      if (pinnedEvidenceOpen) {
        event.preventDefault();
        onClosePinnedEvidence();
        return;
      }

      if (dialogOpen) return;

      event.preventDefault();
      setIsFocused(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dialogOpen, evidenceOpen, isFocused, onCloseEvidence, onClosePinnedEvidence, pinnedEvidenceOpen]);

  return { isFocused, focusConversation, backToInvestigation, toggleFocus };
}
